/**
 * Donation business rules that both clients need.
 *
 * These are pure functions with no I/O. They exist so the phone app and the
 * admin portal agree on validation, and so the client can render an optimistic
 * preview — but note the rule from the master prompt: a preview is not a quote.
 * Anything the donor confirms must carry a server-issued total.
 */

import type { AppConfig, CreateDonationRequest, DonorDetails, Meal, Quote } from './contracts';
import { fromMajor, toISODate, toMajor, type Money } from './format';
import { orderStatusMeta, type OrderStatus, type PaymentStatus } from './status';

// ------------------------------------------------------------------ pricing

/**
 * Optimistic total for UI preview only.
 *
 * Used to keep the quantity tiles responsive while a quote is in flight. The
 * value it produces must never be sent to the server or shown on the Review
 * screen — {@link Quote.totalAmount} is authoritative there.
 */
export function previewTotal(unitPrice: Money, peopleCount: number): Money {
  return {
    amountMinor: unitPrice.amountMinor * Math.max(0, Math.trunc(peopleCount)),
    currency: unitPrice.currency,
  };
}

/** Per-tile prices for the quantity selector; recomputed when the meal changes. */
export function presetPrices(
  meal: Pick<Meal, 'unitPrice'>,
  presets: number[],
): { count: number; total: Money }[] {
  return presets.map((count) => ({ count, total: previewTotal(meal.unitPrice, count) }));
}

/** True when a server quote still matches what the donor is looking at. */
export function quoteMatches(quote: Quote, mealId: string, peopleCount: number): boolean {
  return quote.mealId === mealId && quote.peopleCount === peopleCount;
}

export const quoteExpired = (quote: Quote, now: Date = new Date()): boolean =>
  new Date(quote.expiresAt).getTime() <= now.getTime();

// --------------------------------------------------------------- validation

export type FieldErrors = Record<string, string>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
/** Pakistani mobile: 03xxxxxxxxx or +923xxxxxxxxx. */
const PK_MOBILE_RE = /^(?:\+92|0)3\d{9}$/;

/**
 * A Pakistani mobile account in the form Easypaisa writes it, `03001234567`,
 * or null when the input is not one. Spaces, dashes and a +92 prefix are
 * accepted, since that is how people copy their own number.
 */
export function normaliseWalletMsisdn(input: string | null | undefined): string | null {
  if (!input) return null;
  let digits = input.replace(/\D/g, '');
  if (digits.startsWith('0092')) digits = digits.slice(4);
  else if (digits.startsWith('92') && digits.length === 12) digits = digits.slice(2);
  if (digits.length === 10 && digits.startsWith('3')) digits = `0${digits}`;
  return /^03\d{9}$/.test(digits) ? digits : null;
}

export function validatePeopleCount(count: number, config: AppConfig): string | null {
  if (!Number.isInteger(count)) return 'Enter a whole number of people.';
  if (count < config.people.min) {
    return `You can feed a minimum of ${config.people.min} people.`;
  }
  if (count > config.people.max) {
    return `You can feed a maximum of ${config.people.max} people in one donation.`;
  }
  return null;
}

export function validateDistributionDate(
  value: string,
  config: AppConfig,
  today: Date = new Date(),
): string | null {
  if (!value) return 'Choose a distribution date.';
  const chosen = new Date(`${value}T00:00:00`);
  if (Number.isNaN(chosen.getTime())) return 'Choose a valid distribution date.';

  const start = new Date(today);
  start.setHours(0, 0, 0, 0);
  const dayMs = 86_400_000;
  const daysAhead = Math.round((chosen.getTime() - start.getTime()) / dayMs);

  if (daysAhead < config.distributionDate.minDaysAhead) {
    const d = config.distributionDate.minDaysAhead;
    return d === 0
      ? 'Distribution cannot be scheduled in the past.'
      : `Distribution must be at least ${d} day${d === 1 ? '' : 's'} from today.`;
  }
  if (daysAhead > config.distributionDate.maxDaysAhead) {
    return `Distribution can be scheduled up to ${config.distributionDate.maxDaysAhead} days ahead.`;
  }
  if (config.distributionDate.blackoutDates.includes(toISODate(chosen))) {
    return 'Distribution is unavailable on this date. Please choose another.';
  }
  return null;
}

/**
 * Donor details are optional, so this validates *shape*, not presence.
 *
 * Nothing here may block a donation for a field left blank — a donor who wants
 * to give anonymously and tell us nothing is a supported case, not an error.
 * What is still checked is a value that was supplied and is wrong, because
 * silently storing an unusable mobile number helps nobody.
 */
/**
 * Whether an update could actually be sent.
 *
 * Asking for updates without leaving an address is not an error — every field
 * on that screen is optional and the header says so. It used to block the
 * donation, which meant a donor who never typed an email could be stopped by
 * a box that happens to default to ticked. The wish is simply dropped at
 * checkout instead, so the charity never records a promise it cannot keep.
 */
export function updatesReachable(donor: DonorDetails): boolean {
  return donor.receiveUpdates && Boolean(donor.email?.trim());
}

export function validateDonor(donor: DonorDetails, config: AppConfig): FieldErrors {
  const errors: FieldErrors = {};

  const fullName = donor.fullName?.trim() ?? '';
  if (fullName && fullName.length < 2) {
    errors.fullName = 'Enter your full name, or leave it blank to stay anonymous.';
  }

  const mobile = donor.mobileNumber?.trim() ?? '';
  if (mobile && !PK_MOBILE_RE.test(mobile.replace(/[\s-]/g, ''))) {
    errors.mobileNumber = 'Enter a valid mobile number, for example 03001234567.';
  }

  if (donor.email && !EMAIL_RE.test(donor.email.trim())) {
    errors.email = 'Enter a valid email address.';
  }
  // Either a city from the list, or one the donor typed because theirs is
  // not on it. A donor outside the served cities still has to say where.
  if (config.requireCity && !donor.cityId && !donor.cityName?.trim()) {
    errors.cityId = 'Select your city.';
  }

  return errors;
}

export function validatePurpose(
  purpose: CreateDonationRequest['purpose'],
  details: string | null,
  config: AppConfig,
): string | null {
  const option = config.purposes.find((p) => p.code === purpose);
  if (!option) return 'Select a purpose for your donation.';
  if (option.requiresDetails && !details?.trim()) {
    return `Tell us a little more about "${option.label}".`;
  }
  if (details && details.length > 280) {
    return 'Keep this under 280 characters.';
  }
  return null;
}

/** Whole-form validation before enabling the Confirm action. */
export function validateDonationDraft(
  draft: {
    mealId: string | null;
    peopleCount: number | null;
    purpose: CreateDonationRequest['purpose'] | null;
    purposeDetails: string | null;
    distributionDate: string | null;
    donor: DonorDetails | null;
    paymentMethodCode: string | null;
  },
  config: AppConfig,
  today?: Date,
): FieldErrors {
  const errors: FieldErrors = {};

  if (!draft.mealId) errors.mealId = 'Choose a meal to donate.';

  if (draft.peopleCount === null) {
    errors.peopleCount = 'Choose how many people to feed.';
  } else {
    const e = validatePeopleCount(draft.peopleCount, config);
    if (e) errors.peopleCount = e;
  }

  if (!draft.purpose) {
    errors.purpose = 'Select a purpose for your donation.';
  } else {
    const e = validatePurpose(draft.purpose, draft.purposeDetails, config);
    if (e) errors.purpose = e;
  }

  const dateError = validateDistributionDate(draft.distributionDate ?? '', config, today);
  if (dateError) errors.distributionDate = dateError;

  if (!draft.donor) {
    errors.fullName = 'Enter your details.';
  } else {
    Object.assign(errors, validateDonor(draft.donor, config));
  }

  if (!draft.paymentMethodCode) errors.paymentMethodCode = 'Choose a payment method.';

  return errors;
}

export const isValid = (errors: FieldErrors): boolean => Object.keys(errors).length === 0;

// ------------------------------------------------------------ state machine

/**
 * Permitted order transitions. Mirrors the server's authority exactly so the
 * admin UI can grey out impossible actions — but the server re-checks every
 * transition; this map is a convenience, never the enforcement point.
 */
export const ALLOWED_TRANSITIONS: Record<OrderStatus, readonly OrderStatus[]> = {
  NEW: ['PAYMENT_CONFIRMED', 'PAYMENT_FAILED', 'CANCELLED'],
  PAYMENT_CONFIRMED: ['ACCEPTED', 'CANCELLED', 'REFUND_PENDING'],
  ACCEPTED: ['PREPARING', 'CANCELLED', 'REFUND_PENDING'],
  PREPARING: ['READY_FOR_DISTRIBUTION', 'CANCELLED'],
  READY_FOR_DISTRIBUTION: ['DISTRIBUTED', 'CANCELLED'],
  DISTRIBUTED: ['COMPLETED'],
  COMPLETED: [],
  PAYMENT_FAILED: ['NEW', 'CANCELLED'],
  CANCELLED: [],
  REFUND_PENDING: ['REFUNDED'],
  REFUNDED: [],
};

export const canTransition = (from: OrderStatus, to: OrderStatus): boolean =>
  ALLOWED_TRANSITIONS[from].includes(to);

export const nextStatuses = (from: OrderStatus): readonly OrderStatus[] =>
  ALLOWED_TRANSITIONS[from];

/**
 * Whether distribution proof may be shown to this donor.
 *
 * Both conditions must hold: the donation reached distribution, and the donor
 * consented on that donation. The server enforces the same rule by omitting
 * photo URLs — this only decides whether to render the section.
 */
export const canShowProofPhotos = (
  status: OrderStatus,
  shareDistributionPhotos: boolean,
): boolean =>
  shareDistributionPhotos &&
  (status === 'DISTRIBUTED' || status === 'COMPLETED');

// ------------------------------------------------------------------ helpers

/** Build the receipt rows shown on the Donation Receipt screen, in order. */
/**
 * The rows of a receipt, in the order they are read.
 *
 * One builder so the phone receipt, the web receipt and anything printed
 * later cannot drift. It holds no secrets: a payment's provider reference is
 * safe to print, a card number or a merchant key would not be, and neither is
 * ever carried on a donation.
 *
 * A donor who gave no name renders as a dash rather than as "Anonymous" —
 * choosing to be anonymous and simply not filling a field in are different
 * things, and a receipt should not assert the first when it saw the second.
 */
export function receiptRows(
  donation: {
    publicReference: string;
    meal: { name: string };
    peopleCount: number;
    unitPrice: Money;
    mealSubtotal: Money;
    deliveryFee: Money;
    totalAmount: Money;
    purposeLabel: string;
    distributionDate: string;
    paymentMethod: { name: string } | null;
    transactionReference: string | null;
    donationDate: string;
    donorName?: string | null;
    anonymous?: boolean;
    orderStatus?: OrderStatus;
    paymentStatus?: PaymentStatus;
  },
  fmt: { money: (m: Money) => string; date: (d: string) => string; dateTime: (d: string) => string },
): { label: string; value: string }[] {
  const rows: { label: string; value: string }[] = [
    { label: 'Donation ID', value: donation.publicReference },
    { label: 'Date & Time', value: fmt.dateTime(donation.donationDate) },
    {
      label: 'Donor',
      value: donation.anonymous ? 'Anonymous' : (donation.donorName?.trim() || '—'),
    },
    { label: 'Meal', value: donation.meal.name },
    { label: 'People', value: String(donation.peopleCount) },
    { label: 'Price per person', value: fmt.money(donation.unitPrice) },
  ];

  // The fee only earns a line when it was charged; a zero row on every
  // charity receipt is noise that makes the real ones easier to miss.
  if (donation.deliveryFee.amountMinor > 0) {
    rows.push({ label: 'Meals subtotal', value: fmt.money(donation.mealSubtotal) });
    rows.push({ label: 'Delivery fee', value: fmt.money(donation.deliveryFee) });
  }

  rows.push({ label: 'Total paid', value: fmt.money(donation.totalAmount) });
  rows.push({ label: 'Purpose', value: donation.purposeLabel });
  rows.push({ label: 'Distribution Date', value: fmt.date(donation.distributionDate) });
  rows.push({ label: 'Payment Method', value: donation.paymentMethod?.name ?? '—' });
  rows.push({ label: 'Transaction ID', value: donation.transactionReference ?? '—' });

  if (donation.paymentStatus) {
    rows.push({ label: 'Payment Status', value: statusLabel(donation.paymentStatus) });
  }
  if (donation.orderStatus) {
    rows.push({ label: 'Order Status', value: orderStatusMeta[donation.orderStatus].label });
  }
  return rows;
}

function statusLabel(status: PaymentStatus): string {
  return status
    .split('_')
    .map((part: string) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');
}

export { fromMajor, toMajor };
